import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { MapPin, Star, Filter, HardHat, CheckCircle2, Crosshair, AlertTriangle } from 'lucide-react';
import { Slider } from '../components/ui/slider';
import { SEOHead } from '../components/SEOHead';
import { CompanyStatusBadge } from '../components/CompanyStatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContractorsPage = () => {
  const { t } = useTranslation();
  const [contractors, setContractors] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [filters, setFilters] = useState({
    trade: '',
    city: '',
    country: '',
    min_rating: '',
    is_available: false,
    is_verified: false,
    hide_flagged: false,
    use_location: false,
    radius_km: 50
  });
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const res = await axios.get(`${API}/trades`);
        setTrades(res.data);
      } catch (error) {
        console.error('Error fetching trades:', error);
      }
    };
    fetchTrades();
  }, []);

  useEffect(() => {
    const fetchContractors = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.trade) params.append('trade', filters.trade);
        if (filters.city) params.append('city', filters.city);
        if (filters.country) params.append('country', filters.country);
        if (filters.min_rating) params.append('min_rating', filters.min_rating);
        if (filters.is_available) params.append('is_available', 'true');
        if (filters.is_verified) params.append('is_verified', 'true');
        if (filters.hide_flagged) params.append('hide_flagged', 'true');
        if (filters.use_location && userLocation) {
          params.append('lat', userLocation.lat);
          params.append('lng', userLocation.lng);
          params.append('radius_km', filters.radius_km);
        }

        const res = await axios.get(`${API}/contractors?${params.toString()}`);
        setContractors(res.data.contractors || []);
        setTotal(res.data.total || 0);
      } catch (error) {
        console.error('Error fetching contractors:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContractors();
  }, [filters, userLocation]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      trade: '',
      city: '',
      country: '',
      min_rating: '',
      is_available: false,
      is_verified: false,
      hide_flagged: false,
      use_location: false,
      radius_km: 50
    });
    setUserLocation(null);
  };

  const handleGeolocate = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          setFilters(prev => ({ ...prev, use_location: true }));
        },
        () => {
          // Default to Brussels if geolocation denied
          setUserLocation({ lat: 50.8503, lng: 4.3517 });
          setFilters(prev => ({ ...prev, use_location: true }));
        }
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8">
      <SEOHead
        title="Sous-traitants BTP qualifiés - Belgique, Luxembourg, France"
        description="Annuaire des sous-traitants vérifiés du bâtiment. Maçons, couvreurs, plombiers, électriciens. Consultez les avis, certifications et disponibilités."
        path="/contractors"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <p className="text-overline mb-2">{t('contractors.title')}</p>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-[#111827] tracking-tight">
            {t('contractors.title')}
          </h1>
          <p className="text-[#4B5563] mt-2">{total} sous-traitants disponibles</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    {t('common.filter')}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    Effacer
                  </Button>
                </div>

                <div className="space-y-6">
                  {/* Trade Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('contractors.trades')}</label>
                    <Select 
                      value={filters.trade || "all"} 
                      onValueChange={(value) => handleFilterChange('trade', value === "all" ? '' : value)}
                    >
                      <SelectTrigger data-testid="filter-trade">
                        <SelectValue placeholder="Tous les métiers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les métiers</SelectItem>
                        {trades.map((trade) => (
                          <SelectItem key={trade.id} value={trade.id}>
                            {t(`trades.${trade.id}`) || trade.name_fr}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* City Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('jobs.city')}</label>
                    <Input
                      placeholder="Rechercher une ville..."
                      value={filters.city}
                      onChange={(e) => handleFilterChange('city', e.target.value)}
                      className="input-field"
                      data-testid="filter-city"
                    />
                  </div>

                  {/* Country Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('jobs.country')}</label>
                    <Select 
                      value={filters.country || "all"} 
                      onValueChange={(value) => handleFilterChange('country', value === "all" ? '' : value)}
                    >
                      <SelectTrigger data-testid="filter-country">
                        <SelectValue placeholder="Tous les pays" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les pays</SelectItem>
                        <SelectItem value="Belgium">Belgique</SelectItem>
                        <SelectItem value="Luxembourg">Luxembourg</SelectItem>
                        <SelectItem value="France">France</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Rating Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('contractors.rating')}</label>
                    <Select 
                      value={filters.min_rating || "all"} 
                      onValueChange={(value) => handleFilterChange('min_rating', value === "all" ? '' : value)}
                    >
                      <SelectTrigger data-testid="filter-rating">
                        <SelectValue placeholder="Toutes notes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes notes</SelectItem>
                        <SelectItem value="4">4+ étoiles</SelectItem>
                        <SelectItem value="3">3+ étoiles</SelectItem>
                        <SelectItem value="2">2+ étoiles</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Available Filter */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="available"
                      checked={filters.is_available}
                      onCheckedChange={(checked) => handleFilterChange('is_available', checked)}
                      data-testid="filter-available"
                    />
                    <label htmlFor="available" className="text-sm font-medium cursor-pointer">
                      {t('common.available')} uniquement
                    </label>
                  </div>

                  {/* Verified Filter */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="verified"
                      checked={filters.is_verified}
                      onCheckedChange={(checked) => handleFilterChange('is_verified', checked)}
                      data-testid="filter-verified"
                    />
                    <label htmlFor="verified" className="text-sm font-medium cursor-pointer flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      {t('common.verified')} uniquement
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="hide_flagged"
                      checked={filters.hide_flagged}
                      onCheckedChange={(checked) => handleFilterChange('hide_flagged', checked)}
                      data-testid="filter-hide-flagged"
                    />
                    <label htmlFor="hide_flagged" className="text-sm font-medium cursor-pointer flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4 text-orange-500" />
                      Masquer les entreprises signalées (faillite, PRJ)
                    </label>
                  </div>

                  {/* Geolocation Radius Filter */}
                  <div className="pt-4 border-t">
                    <label className="text-sm font-medium mb-2 block flex items-center gap-1">
                      <Crosshair className="w-4 h-4 text-[#1E3A8A]" />
                      Recherche par rayon
                    </label>
                    {!filters.use_location ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full gap-2"
                        onClick={handleGeolocate}
                        data-testid="geolocate-button"
                      >
                        <Crosshair className="w-4 h-4" />
                        Utiliser ma position
                      </Button>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[#6B7280]">Rayon</span>
                          <span className="font-medium text-[#111827]">{filters.radius_km} km</span>
                        </div>
                        <Slider
                          value={[filters.radius_km]}
                          onValueChange={(val) => handleFilterChange('radius_km', val[0])}
                          min={5}
                          max={200}
                          step={5}
                          className="w-full"
                          data-testid="radius-slider"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            setUserLocation(null);
                            setFilters(prev => ({ ...prev, use_location: false }));
                          }}
                        >
                          Désactiver
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </aside>

          {/* Contractors Grid */}
          <main className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A8A]"></div>
              </div>
            ) : contractors.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-[#4B5563]">{t('common.noResults')}</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {contractors.map((contractor) => (
                  <Link to={`/contractors/${contractor.id}`} key={contractor.id}>
                    <Card className="card-surface h-full" data-testid={`contractor-item-${contractor.id}`}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="w-16 h-16 bg-[#1E3A8A]/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <HardHat className="w-8 h-8 text-[#1E3A8A]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-heading font-bold text-lg text-[#111827] truncate">
                                {contractor.company_name}
                              </h3>
                              {contractor.is_verified && (
                                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 mb-3">
                              {contractor.review_count > 0 ? (
                                <>
                                  <div className="flex items-center gap-1">
                                    <Star className="w-4 h-4 text-[#F59E0B] fill-[#F59E0B]" />
                                    <span className="font-medium">{contractor.rating?.toFixed(1)}</span>
                                  </div>
                                  <span className="text-sm text-[#4B5563]">({contractor.review_count} avis)</span>
                                </>
                              ) : (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">Nouveau</Badge>
                              )}
                              {contractor.is_verified && (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Vérifié</Badge>
                              )}
                              <CompanyStatusBadge status={contractor.kbo_status} compact />
                              {contractor.is_available ? (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">{t('common.available')}</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-600 text-xs">{t('common.unavailable')}</Badge>
                              )}
                            </div>

                            {/* Profile completion indicator */}
                            {(() => {
                              const fields = [contractor.city, contractor.trades?.length, contractor.vat_number, contractor.phone, contractor.description];
                              const filled = fields.filter(Boolean).length;
                              const pct = Math.round((filled / fields.length) * 100);
                              return pct < 100 ? (
                                <div className="mb-3">
                                  <div className="flex items-center gap-2 text-xs text-[#6B7280]">
                                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                      <div className="h-full bg-[#1E3A8A] rounded-full" style={{width: `${pct}%`}} />
                                    </div>
                                    <span>Profil {pct}%</span>
                                  </div>
                                </div>
                              ) : null;
                            })()}

                            <div className="flex flex-wrap gap-1 mb-3">
                              {contractor.trades?.slice(0, 3).map((trade, i) => (
                                <Badge key={trade || i} className="badge-trade text-xs">
                                  {t(`trades.${trade}`) || trade}
                                </Badge>
                              ))}
                              {contractor.trades?.length > 3 && (
                                <Badge variant="secondary" className="text-xs">+{contractor.trades.length - 3}</Badge>
                              )}
                            </div>

                            <div className="flex items-center gap-4 text-sm text-[#4B5563]">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {contractor.city}, {contractor.country}
                              </span>
                              {contractor.distance_km !== undefined && (
                                <span className="flex items-center gap-1 text-[#1E3A8A] font-medium">
                                  <Crosshair className="w-3 h-3" />
                                  {contractor.distance_km} km
                                </span>
                              )}
                              {contractor.years_experience > 0 && (
                                <span>{contractor.years_experience} ans exp.</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ContractorsPage;
